import React, { useState, useMemo } from 'react';
import { Mail, Loader2, Video, Calendar, ArrowRight, Clock, NotebookPen, FileText, BarChart3, Trophy, BookOpenCheck, CheckCircle2 } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import Seo from '@/components/shared/Seo';

const STATUS_TONE = {
  scheduled: 'bg-amber-50 text-amber-800 border-amber-200',
  live:      'bg-emerald-50 text-emerald-800 border-emerald-200',
  completed: 'bg-gray-100 text-gray-700 border-gray-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
  missed:    'bg-red-50 text-red-700 border-red-200',
};

const StatCard = ({ label, value, suffix, icon: Icon, tone = 'gray' }) => (
  <div className={`rounded-xl border p-4 ${tone === 'red' ? 'bg-red-50 border-red-100' : tone === 'green' ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-gray-200'}`}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className={`h-3.5 w-3.5 ${tone === 'red' ? 'text-red-600' : tone === 'green' ? 'text-emerald-600' : 'text-gray-400'}`} />}
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    </div>
    <div className="text-2xl font-bold text-gray-900">{value}{suffix && <span className="text-sm font-medium text-gray-500 ml-1">{suffix}</span>}</div>
  </div>
);

const ClassCard = ({ it, kind, expandable = true }) => {
  const [expanded, setExpanded] = useState(false);
  const joinUrl = kind === 'student' ? it.student_join_url : it.tutor_join_url;
  const counterpart = kind === 'student' ? `with ${it.tutor_name}` : `for ${it.student_name}`;
  const s = it.summary || {};
  const isPast = ['completed', 'missed', 'cancelled'].includes(it.status);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={`${kind}-class-${it.booking_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${STATUS_TONE[it.status] || STATUS_TONE.scheduled}`}>{it.status}</span>
            {it.payment_required && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${it.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                {it.payment_status === 'paid' ? 'Paid' : it.payment_status === 'waived' ? 'Waived' : 'Awaiting payment'}
              </span>
            )}
            {s.has_homework && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border bg-blue-50 text-blue-700 border-blue-200">Homework set</span>}
            {s.has_recording && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border bg-purple-50 text-purple-700 border-purple-200">Recording</span>}
          </div>
          <div className="font-bold text-base text-gray-900">{it.subject}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-1">
            <Clock className="h-3.5 w-3.5" />
            {new Date(it.start_time).toLocaleString()} → {new Date(it.end_time).toLocaleTimeString()}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{counterpart}</div>
          <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-500">
            <span className="flex items-center gap-1"><Video className="h-3 w-3" />{s.joins ?? 0} joins</span>
            <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{s.resource_count ?? 0} resources</span>
            {s.has_lesson_notes && <span className="flex items-center gap-1 text-emerald-700"><NotebookPen className="h-3 w-3" />Notes saved</span>}
          </div>
        </div>
        <div className="flex flex-col sm:items-end gap-2">
          {joinUrl && !isPast && (
            <a href={joinUrl}
              className="h-11 px-5 rounded-full bg-black hover:bg-gray-900 text-white text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap"
              data-testid={`${kind}-join-${it.booking_id}`}>
              <Video className="h-4 w-4" /> {kind === 'tutor' ? 'Enter classroom' : 'Join class'}
            </a>
          )}
          {joinUrl && isPast && (
            <a href={joinUrl}
              className="h-9 px-4 rounded-full border border-gray-300 text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap hover:bg-gray-50">
              Review session
            </a>
          )}
          {expandable && (s.has_lesson_notes || s.resource_count > 0 || s.has_homework) && (
            <button onClick={() => setExpanded((x) => !x)} className="text-[11px] uppercase tracking-wider font-semibold text-red-700">
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-700 space-y-2">
          {s.has_homework && (
            <div className="flex items-start gap-2">
              <BookOpenCheck className="h-4 w-4 text-blue-700 mt-0.5" />
              <span><strong>Homework is set.</strong> Open the classroom for full details.</span>
            </div>
          )}
          {s.has_lesson_notes && (
            <div className="flex items-start gap-2">
              <NotebookPen className="h-4 w-4 text-emerald-700 mt-0.5" />
              <span><strong>Lesson notes available.</strong> Open the classroom to read your tutor's summary.</span>
            </div>
          )}
          {s.has_recording && (
            <div className="flex items-start gap-2">
              <Video className="h-4 w-4 text-purple-700 mt-0.5" />
              <span><strong>Approved recording available.</strong> Open the classroom to play it back.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const buildDashboardPage = ({ title, subtitle, kind, stats }) => {
  return function DashboardPage() {
    const [email, setEmail] = useState('');
    const [items, setItems] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('upcoming');
    const [mobile, setMobile] = useState(false);

    const lookup = async (e) => {
      e?.preventDefault?.();
      if (!email.trim()) return;
      setLoading(true);
      try {
        const fn = kind === 'student' ? classroomApi.lookupStudent : classroomApi.lookupTutor;
        const r = await fn(email.trim().toLowerCase());
        setItems(r.items || []);
        setAnalytics(r.analytics || null);
        if ((r.items || []).length === 0) {
          toast.message('No classes yet — Winhouse will email you when one is scheduled.');
        }
      } catch (err) { toast.error(err.message || 'Lookup failed'); }
      finally { setLoading(false); }
    };

    const filtered = useMemo(() => {
      if (!items) return [];
      if (tab === 'upcoming') return items.filter((x) => ['scheduled', 'live'].includes(x.status));
      if (tab === 'completed') return items.filter((x) => x.status === 'completed');
      return items.filter((x) => ['cancelled', 'missed'].includes(x.status));
    }, [items, tab]);

    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid={`${kind}-dashboard-page`}>
        <Seo title={`${title} · Winhouse`} canonical={`/${kind}/classes`} />
        <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
        <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12 w-full">
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">Winhouse Classroom</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
            <p className="text-gray-600 text-sm mt-1">{subtitle}</p>
          </div>

          {!items && (
            <form onSubmit={lookup} className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 mb-6 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex items-center gap-2 border border-gray-200 rounded-xl px-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="flex-1 h-11 outline-none text-sm bg-transparent"
                  data-testid={`${kind}-classes-email`} />
              </div>
              <button type="submit" disabled={loading || !email.trim()}
                className="h-11 px-6 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid={`${kind}-classes-lookup`}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Open my dashboard
              </button>
            </form>
          )}

          {items && analytics && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid={`${kind}-analytics`}>
              <StatCard label={kind === 'tutor' ? 'Teaching hours' : 'Study hours'} value={analytics.hours} suffix="hrs" icon={Clock} tone="green" />
              <StatCard label="Upcoming" value={analytics.upcoming} icon={Calendar} />
              <StatCard label="Completed" value={analytics.completed} icon={CheckCircle2} />
              <StatCard label="Attendance" value={analytics.attendance_pct} suffix="%" icon={Trophy} tone={analytics.attendance_pct >= 80 ? 'green' : 'red'} />
            </div>
          )}

          {items && (
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              {[
                { k: 'upcoming', l: `Upcoming · ${items.filter((x) => ['scheduled', 'live'].includes(x.status)).length}` },
                { k: 'completed', l: `Completed · ${items.filter((x) => x.status === 'completed').length}` },
                { k: 'other', l: `Other · ${items.filter((x) => ['cancelled', 'missed'].includes(x.status)).length}` },
              ].map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)}
                  className={`h-9 px-4 rounded-full text-xs font-semibold uppercase tracking-wider border ${tab === t.k ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`${kind}-tab-${t.k}`}>
                  {t.l}
                </button>
              ))}
              <button onClick={() => { setItems(null); setAnalytics(null); setEmail(''); }} className="ml-auto text-[11px] uppercase tracking-wider font-semibold text-gray-500 hover:text-gray-900">Switch email</button>
            </div>
          )}

          {items && filtered.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center">
              <Calendar className="h-6 w-6 mx-auto text-gray-400 mb-3" />
              <div className="text-sm font-semibold text-gray-700 mb-1">Nothing in this tab</div>
              <div className="text-xs text-gray-500">Try another tab or check back after your next class.</div>
            </div>
          )}

          {filtered.length > 0 && (
            <div className="space-y-3" data-testid={`${kind}-classes-list`}>
              {filtered.map((it) => <ClassCard key={it.booking_id} it={it} kind={kind} />)}
            </div>
          )}
        </main>
        <Footer />
      </div>
    );
  };
};

export const StudentClassesPage = buildDashboardPage({
  title: 'My Winhouse Dashboard',
  subtitle: 'See your upcoming classes, past sessions, lesson notes, homework, and study hours — all in one place.',
  kind: 'student',
});

export const TutorClassesPage = buildDashboardPage({
  title: 'Tutor Dashboard',
  subtitle: 'Manage your teaching schedule, materials, and student progress.',
  kind: 'tutor',
});
